<template>
  <div class="form-section">
    <SectionSeparatorWrapper :separator="false" headerText="My cover" headerRequiredFieldsText="indicates required field">
      <h5 class="section-heading">Policy holder</h5>
      <FormField key="composition" :labelText="refData.coverComposition.label" :helperText="refData.coverComposition.helpText" required>
        <DropdownSelect :items="compositionList" v-model="v$.step2.composition.$model" :initialSelect="compositionInitialSelect" />
        <!-- Rebate applicant being terminated message -->
        <template v-if="isPartnerRebateApplicant && showPartner">
          <Alert
            alertType="error"
            class="inline-message"
            message="You can't remove the partner on this policy as they currently hold the rebate on this policy. Please contact Defence Health to either switch the rebate applicant, withdraw the rebate or terminate the partner on the policy."
          ></Alert>
        </template>
      </FormField>
      <div v-if="isChildrenOptionShow">
        <FormField
          id="numberOfChildren"
          :labelText="refData.numberOfChildren.label"
          :helperText="refData.numberOfChildren.helpText"
          placeholder="Enter # of children"
          step="1"
          v-model="v$.step2.numberOfChildren.$model"
          type="number"
          class="half-width"
          @input="v$.step2.numberOfChildren.$touch"
          name="numberOfChildren"
          :error="v$.step2.numberOfChildren.$error"
          :errorMessage="calcNumOfChildrenErrorMessage"
        ></FormField>
        <div v-if="isFamily">
          <FormField :labelText="refData.familyPlus.label" :helperText="refData.familyPlus.helpText" required>
            <ul class="radio-group">
              <li v-for="option in [true, false]" :key="`familyPlus-${option}`">
                <input type="radio" :id="`familyPlus-${option}`" :value="option" name="familyPlus" v-model="v$.step2.familyPlus.$model" />
                <label :for="`familyPlus-${option}`">{{ option === true ? 'Yes' : 'No' }}</label>
              </li>
            </ul>
            <Alert
              class="student-warning"
              v-if="isFamilyOrSingleParent && isCoveringNonStudentDependent"
              message="Non-student dependents will not be eligible to be covered"
              alertType="error"
            />
          </FormField>
        </div>
      </div>
      <div v-if="isSingle">
        <FormField key="myCircumstances" :labelText="refData.myCircumstances.label" :helperText="refData.myCircumstances.helpText" required>
          <ul class="radio-group">
            <li v-for="option in refData.myCircumstances.options" :key="option.returnValue">
              <input type="radio" :id="`myCircumstances-${option.returnValue}`" :value="option.returnValue" name="myCircumstances" v-model="v$.step2.myCircumstances.$model" />
              <label :for="`myCircumstances-${option.returnValue}`">{{ option.display }}</label>
            </li>
          </ul>
        </FormField>
      </div>
      <FormField :labelText="getIncomeTier.label" :helperText="getIncomeTier.helpText" required>
        <DropdownSelect :items="getIncomeTier.options" :initialSelect="incomeTierInitialSelect" v-model="v$.step2.rebateTier.$model" />
      </FormField>
      <!-- Help text is currently a closed cover warning, displayed if H&E and closd product-->
      <FormField
        :labelText="refData.coverType.label"
        :helperText="step2.coverType && isHospitalAndExtras && (isClosedHospitalProduct || isClosedExtrasProduct) ? refData.coverType.helpText : ''"
        required
      >
        <ul class="radio-group">
          <li v-for="option in coverOptions" :key="option.returnValue">
            <input type="radio" :id="`coverType-${option.returnValue}`" :value="option.returnValue" name="coverType" v-model="v$.step2.coverType.$model" />
            <label :for="`coverType-${option.returnValue}`">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
      <!-- Only display BOTH if H&E are both closed -->
      <FormField
        v-if="isHospitalAndExtras && (isClosedHospitalProduct || isClosedExtrasProduct)"
        :labelText="refData.reviewCover.label"
        :helperText="refData.reviewCover.helpText"
        required
      >
        <ul class="radio-group">
          <li v-for="option in reviewCoverOptions" :key="option.returnValue">
            <input type="radio" :id="`reviewCover-${option.returnValue}`" :value="option.returnValue" name="reviewCover" v-model="v$.step2.reviewCover.$model" />
            <label :for="`reviewCover-${option.returnValue}`">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
    </SectionSeparatorWrapper>
    <!-- Policy holder health insurance history section  -->
    <template v-if="wantsHospital">
      <HealthInsuranceHistorySection
        :coverStartDate="changeCoverData.step1.startDate"
        :eligibleForABD="wantsHospital"
        :portalAgeBasedDiscount="policyHolderState === HospitalSituation.REAPPLYING ? step2.policyHolder.portalAgeBasedDiscount : null"
        :refData="refData"
        :dob="step2.policyHolder.dob"
        :initialData="policyHolderLHCABD"
        :products="membershipProducts"
        parentClass="policyHolder"
        :isChangeCover="true"
        @abdLhcUpdated="getPolicyHolderABDAndLHCResponse"
        @updateEligibleForLHC="policyHolderUpdateEligibleForLHC"
        :readOnlyABD="policyHolderState === HospitalSituation.RETAINING"
        :readOnlyLHC="policyHolderState === HospitalSituation.RETAINING"
      />
    </template>
    <!-- PolicyHolder Veteran Card section -->
    <SectionSeparatorWrapper>
      <div class="form-section">
        <div v-if="showVeteranCard">
          <h3 class="h6 sub-heading-underlined">Veteran Card</h3>
          <FormField :labelText="refData.veteranCardSelectionLabel.informationText" :helperText="refData.veteranCard.helpText">
            <ul class="radio-group large">
              <li v-for="option in displayRadioOptions" :key="`veteran-card-policy-holder-${option.display}`">
                <input
                  type="radio"
                  :id="`veteran-card-policy-holder-${option.display}`"
                  v-model.lazy="step2.policyHolder.veteranCard.haveVeteranCard"
                  :name="`veteran-card-policy-holder-${option.display}`"
                  :value="option.returnValue"
                />
                <label :for="`veteran-card-policy-holder-${option.display}`">{{ option.display }}</label>
              </li>
            </ul>
          </FormField>
          <FormField v-if="step2.policyHolder.veteranCard.haveVeteranCard" labelText="Which Veteran card do you hold?" class="form-section veteran-card-type">
            <ul class="radio-group large form-section">
              <li v-for="option in refData.veteranCard.options" :key="option.returnValue">
                <input
                  type="radio"
                  :id="`veteran-card-policy-holder-${option.display}`"
                  :value="option.returnValue"
                  :name="`veteran-card-policy-holder-${option.display}`"
                  v-model.lazy="v$.step2.policyHolder.veteranCard.veteranCardType.$model"
                />
                <label :for="`veteran-card-policy-holder-${option.display}`">{{ option.display }}</label>
              </li>
            </ul>
          </FormField>
          <p v-if="step2.policyHolder.veteranCard.haveVeteranCard" v-html="veteranCardSupportText(step2.policyHolder.veteranCard.veteranCardType)" />
        </div>
      </div>
    </SectionSeparatorWrapper>
    <!-- Partner sections -->
    <SectionSeparatorWrapper v-if="selectedCompositionSupportsPartner">
      <div class="form-section">
        <h3 class="h5">Partner</h3>
        <!-- Existing partner section -->
        <template v-if="currentMembershipHasPartner">
          <div class="member-container">
            <div class="member-icon">
              <svg>
                <use xlink:href="/assets/public/images/icons.svg#member" />
              </svg>
            </div>
            <div>
              <h3 class="h6">{{ step2.partner.firstName }} {{ step2.partner.lastName }}</h3>
              <span v-if="!doesPartnerHaveKeepPrivateOn">
                <strong>Date of birth:</strong>
                {{ step2.partner.dob }}
              </span>
            </div>
          </div>
          <div>
            <FormField labelText="I would like to keep this adult on the membership as a partner" required>
              <ul class="radio-group large form-section">
                <li v-for="option in [true, false]" :key="`partner-keepAdult-${option}`">
                  <input type="radio" :id="`partner-keepAdult-${option}`" :value="option" name="partner-keepAdult" v-model="v$.step2.partner.keepAdult.$model" />
                  <label :for="`partner-keepAdult-${option}`">{{ option === true ? 'Yes' : 'No' }}</label>
                </li>
              </ul>
              <Alert v-if="!step2.partner.keepAdult" message="This adult will no longer be covered on this policy." />
            </FormField>
          </div>
        </template>
        <!-- New partner -->
        <template v-if="showMyCoverNewPartnerSection">
          <h3 class="h6 sub-heading-underlined">New partner details</h3>
          <p v-html="refData.replacingPartnerHelpText.informationText" />
          <FormField
            id="partnerDOB"
            :labelText="refData.partnerAge.label"
            placeholder="DD / MM / YYYY"
            key="partnerDOB"
            v-maska="'## / ## / ####'"
            type="text"
            maxlength="14"
            v-model.trim="v$.step2.partner.newPartner.DOB.$model"
            @input="v$.step2.partner.newPartner.DOB.$touch"
            autocomplete="off"
            name="partnerDOB"
            :error="v$.step2.partner.newPartner.DOB.$error"
            :errorMessage="v$.step2.partner.newPartner.DOB.$model && calcPartnerErrorMessage"
          ></FormField>
        </template>
        <template v-if="wantsHospital && partnerDOB">
          <HealthInsuranceHistorySection
            :coverStartDate="changeCoverData.step1.startDate"
            :eligibleForABD="wantsHospital"
            :portalAgeBasedDiscount="partnerHospitalState === HospitalSituation.REAPPLYING ? step2.partner.portalAgeBasedDiscount : null"
            :refData="refData"
            :dob="partnerDOB"
            :initialData="partnerLHCABD"
            :products="membershipProducts"
            parentClass="partner"
            :isChangeCover="true"
            @abdLhcUpdated="getPartnerABDAndLHCResponse"
            @updateEligibleForLHC="partnerUpdateEligibleForLHC"
            :readOnlyABD="partnerHospitalState === HospitalSituation.RETAINING"
            :readOnlyLHC="partnerHospitalState === HospitalSituation.RETAINING"
          />
        </template>
        <!-- Existing partner Veteran Card-->
        <template v-if="currentMembershipHasPartner">
          <div v-if="showVeteranCard && step2.partner.keepAdult">
            <h3 class="h6 sub-heading-underlined">Veteran Card</h3>
            <FormField :labelText="refData.veteranCardSelectionLabel.informationText" :helperText="refData.veteranCard.helpText">
              <ul class="radio-group large">
                <li v-for="option in displayRadioOptions" :key="`veteran-card-partner-${option.display}`">
                  <input
                    type="radio"
                    :id="`veteran-card-partner-${option.display}`"
                    v-model.lazy="step2.partner.veteranCard.haveVeteranCard"
                    :name="`veteran-card-partner-${option.display}`"
                    :value="option.returnValue"
                  />
                  <label :for="`veteran-card-partner-${option.display}`">{{ option.display }}</label>
                </li>
              </ul>
            </FormField>
            <FormField v-if="step2.partner.veteranCard.haveVeteranCard" labelText="Which Veteran card do you hold?" class="form-section veteran-card-type">
              <ul class="radio-group large form-section">
                <li v-for="option in refData.veteranCard.options" :key="option.returnValue">
                  <input
                    type="radio"
                    :id="`veteran-card-partner-${option.display}`"
                    :value="option.returnValue"
                    :name="`veteran-card-partner-${option.display}`"
                    v-model.lazy="v$.step2.partner.veteranCard.veteranCardType.$model"
                  />
                  <label :for="`veteran-card-partner-${option.display}`">{{ option.display }}</label>
                </li>
              </ul>
            </FormField>
            <p v-if="step2.partner.veteranCard.haveVeteranCard" v-html="veteranCardSupportText(step2.partner.veteranCard.veteranCardType)" />
          </div>
        </template>
      </div>
      <div v-if="showMyCoverNewPartnerSection">
        <div v-if="eligibleForVeteranCard">
          <h3 class="h6 sub-heading-underlined">Veteran Card</h3>
          <FormField :labelText="refData.veteranCardSelectionLabel.informationText" :helperText="refData.veteranCard.helpText">
            <ul class="radio-group large">
              <li v-for="option in displayRadioOptions" :key="`veteran-card-new-partner-${option.display}`">
                <input
                  type="radio"
                  :id="`veteran-card-new-partner-${option.display}`"
                  v-model.lazy="step2.partner.newPartner.veteranCard.haveVeteranCard"
                  :name="`veteran-card-new-partner-${option.display}`"
                  :value="option.returnValue"
                />
                <label :for="`veteran-card-new-partner-${option.display}`">{{ option.display }}</label>
              </li>
            </ul>
          </FormField>
          <FormField v-if="step2.partner.newPartner.veteranCard.haveVeteranCard" labelText="Which Veteran card do you hold?" class="form-section veteran-card-type">
            <ul class="radio-group large form-section">
              <li v-for="option in refData.veteranCard.options" :key="option.returnValue">
                <input
                  type="radio"
                  :id="`veteran-card-new-partner-${option.display}`"
                  :value="option.returnValue"
                  :name="`veteran-card-new-partner-${option.display}`"
                  v-model.lazy="v$.step2.partner.newPartner.veteranCard.veteranCardType.$model"
                />
                <label :for="`veteran-card-new-partner-${option.display}`">{{ option.display }}</label>
              </li>
            </ul>
          </FormField>
          <p v-if="step2.partner.newPartner.veteranCard.haveVeteranCard" v-html="veteranCardSupportText(step2.partner.newPartner.veteranCard.veteranCardType)" />
        </div>
      </div>
    </SectionSeparatorWrapper>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import { required, requiredIf, integer, minValue, maxValue } from '@vuelidate/validators';
import { overAge, underAge, isPastDate } from '@general/js/utils/dateTimeUtils';
import { DATE_VALIDATOR } from '@general/js/validators';
import { Alert, FormField, SectionSeparatorWrapper } from '@/components';
import { CoverComposition, RebateTier, MyCircumstances, CoverTypeValue, NeedsAnalysisEligibility } from '@general/js/enums';
import { reverseFormatFormDate } from '@general/js/utils';
import { DropdownSelect } from '@general/js/components';
import { isEmptyString, getPartnerDetailsMP, findPolicyHolder } from '@/utils';
import { findDependents } from '@/utils/ChangeProduct/ExtraUtils';
import { VeteranCardTypeOptions, HospitalSituation, OperationType } from '@/enums';
import HealthInsuranceHistorySection from '@general/js/templates/JoinNow/JoinNowSteps/FormSection/HealthInsuranceHistory.vue';
import { formatEaiLhcToObj } from '@/utils/ChangeProduct/GetPremiumPrice/PersonUtils';
import ChangeCoverMixins from '../ChangeCoverMixins';
import CaptureInformationMixins from '../CaptureInformationMixins';
import useVuelidate from '@vuelidate/core';

export default {
  name: 'MyCover',

  setup() {
    return { v$: useVuelidate() };
  },

  mixins: [ChangeCoverMixins, CaptureInformationMixins],

  components: {
    Alert,
    DropdownSelect,
    FormField,
    SectionSeparatorWrapper,
    HealthInsuranceHistorySection,
  },

  props: {
    // UI data for change cover
    refData: {
      type: Object,
      required: true,
    },
    prefillDetails: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      step2: {
        composition: null,
        partnerDOB: '',
        myCircumstances: null,
        rebateTier: null,
        numberOfChildren: null,
        familyPlus: null,
        coverType: null,
        reviewCover: null,
        policyHolder: {
          dob: null,
          lhc: null,
          abd: null,
          portalAgeBasedDiscount: {
            firstTimePHI: null,
            currentlyReceived: null,
            cda: null,
          },
          eligibleForLHC: false,
          abdLhcResponseValid: false,
          veteranCard: {
            haveVeteranCard: null,
            veteranCardType: '',
          },
          existingLHCOverrule: null,
        },
        partner: {
          firstName: null,
          lastName: null,
          dob: null,
          keepAdult: null,
          lhc: null,
          abd: null,
          portalAgeBasedDiscount: {
            firstTimePHI: null,
            currentlyReceived: null,
            cda: null,
          },
          eligibleForLHC: null,
          abdLhcResponseValid: null,
          veteranCard: {
            haveVeteranCard: null,
            veteranCardType: '',
          },
          existingLHCOverrule: null,
          newPartner: {
            DOB: '',
            lhc: null,
            abd: null,
            eligibleForLHC: null,
            existingLHCOverrule: null,
            abdLhcResponseValid: null,
            veteranCard: {
              haveVeteranCard: null,
              veteranCardType: '',
            },
          },
        },
      },
      isActive: false,
      displayRadioOptions: [
        { returnValue: true, display: 'Yes' },
        { returnValue: false, display: 'No' },
      ],
      HospitalSituation,
    };
  },

  beforeMount() {
    if (this.prefillDetails && this.$route.params?.fromCoverSelectionPage !== true) {
      this.initializeData();
      this.step2 = { ...this.myCover, ...this.step2 };
      this.updateMyCover({ ...cloneDeep(this.step2) });
    }
  },

  activated() {
    this.step2 = this.myCover;
    this.isActive = true;
    this.v$.$touch();
    this.$nextTick(() => {
      this.$emit('responseUpdated', {
        response: this.step2,
        isInvalid: this.v$.$invalid || this.v$.$error || !this.abdLhcSectionsValid,
      });
    });
  },

  deactivated() {
    this.isActive = false;
  },

  watch: {
    step2: {
      handler() {
        if (this.isActive) {
          this.v$.$touch();
          this.$nextTick(() => {
            this.$emit('responseUpdated', {
              response: this.step2,
              isInvalid: this.v$.$invalid || this.v$.$error || !this.abdLhcSectionsValid,
            });
          });
        }
      },
      deep: true,
    },
  },

  computed: {
    coverCompositionOptions() {
      // This property is used to filter out composition options, in the "My cover" step in CoC needs analysis based on decision made under DE2E-10414/415. This is a workaround until DDC-3422 is implemented.
      if (this.isPolicyHolderOrPartnerAContributor) {
        return this.refData.coverComposition.options.filter((option) => {
          return ![CoverComposition.COUPLE, CoverComposition.FAMILY, CoverComposition.CHILDREN_ONLY].includes(option.returnValue);
        });
      }
      if (this.isPolicyHolderAResponsibleAdult) {
        return this.refData.coverComposition.options.filter((option) => {
          return option.returnValue === CoverComposition.CHILDREN_ONLY;
        });
      }
      return this.refData.coverComposition.options;
    },

    isChildrenOptionShow() {
      if (this.step2.composition !== null) {
        return [
          CoverComposition.FAMILY,
          CoverComposition.SINGLE_PARENT,
          CoverComposition.CHILDREN_ONLY,
          CoverComposition.FAMILY_PLUS,
          CoverComposition.SINGLE_PARENT_PLUS,
        ].includes(this.step2.composition);
      }
      return false;
    },

    isFamily() {
      if (this.step2.composition !== null) {
        return [CoverComposition.FAMILY, CoverComposition.SINGLE_PARENT, CoverComposition.FAMILY_PLUS, CoverComposition.SINGLE_PARENT_PLUS].includes(this.step2.composition);
      }
      return false;
    },

    isSingle() {
      if (this.step2.composition !== null) {
        return this.step2.composition === CoverComposition.SINGLE;
      }
      return false;
    },

    isFamilyOrSingleParent() {
      if (this.currentComposition === CoverComposition.FAMILY_PLUS || this.currentComposition === CoverComposition.SINGLE_PARENT_PLUS) {
        return true;
      }
      return false;
    },

    isCoveringNonStudentDependent() {
      if (this.step2.familyPlus === false) {
        return true;
      }
      return false;
    },

    calcPartnerErrorMessage() {
      if (!this.selectedCompositionSupportsPartner) {
        return '';
      }
      if (this.v$.step2.partner.newPartner.DOB.DATE_VALIDATOR.$invalid) {
        return 'Invalid date';
      }
      if (this.v$.step2.partner.newPartner.DOB.isPastDate.$invalid) {
        return `Birth date must be today’s date or past date. Please enter today’s date or a past date.`;
      }
      if (this.v$.step2.partner.newPartner.DOB.overAge.$invalid) {
        return `Age cannot be more than 125`;
      }
      if (this.v$.step2.partner.newPartner.DOB.underAge.$invalid) {
        return `A policy holder must be 18 years of age or older.`;
      }
      return `This is a required field`;
    },

    calcNumOfChildrenErrorMessage() {
      if (!this.isChildrenOptionShow) {
        return '';
      }
      if (this.v$.step2.numberOfChildren.minValue.$invalid) {
        return `Number of children cannot be less than one`;
      }
      if (this.v$.step2.numberOfChildren.maxValue.$invalid) {
        return `You can only have up to 15 children on a single policy`;
      }
      if (this.v$.step2.numberOfChildren.integer.$invalid) {
        return `Please enter a valid integer between 1 and 15`;
      }
      return `This is a required field`;
    },

    getIncomeTier() {
      const incomeTierRefData =
        this.step2.composition === MyCircumstances.SINGLE && this.step2.myCircumstances === MyCircumstances.SINGLE
          ? cloneDeep(this.refData.incomeTier)
          : cloneDeep(this.refData.familyIncomeTier);

      incomeTierRefData.options = incomeTierRefData.options.map((tier) => {
        return { name: tier.display, value: tier.returnValue };
      });

      return incomeTierRefData;
    },

    incomeTierInitialSelect() {
      const incomeTierOption = this.getIncomeTier.options.filter((option) => {
        return this.step2.rebateTier === option.value;
      });

      return incomeTierOption[0]?.name;
    },

    compositionList() {
      return this.refData.coverComposition.options
        .filter((composition) => {
          return composition.isShown;
        })
        .map((composition) => {
          return { name: composition.display, value: composition.returnValue };
        });
    },

    compositionInitialSelect() {
      const compositionOption = this.refData.coverComposition.options.filter((option) => {
        return this.step2.composition === option.returnValue;
      });

      return compositionOption[0]?.display;
    },

    isHospitalAndExtras() {
      if (this.step2.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS) {
        return true;
      }
      return false;
    },

    wantsHospital() {
      return this.step2.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS || this.step2.coverType === CoverTypeValue.HOSPITAL_ONLY;
    },

    coverOptions() {
      return this.refData.coverType.options;
    },

    reviewCoverOptions() {
      if (this.isClosedHospitalProduct && this.isClosedExtrasProduct) {
        return this.refData.reviewCover.options;
      }
      return this.refData.reviewCover.options.slice(0, -1);
    },

    // Checks if current hospital product is closed
    isClosedHospitalProduct() {
      if (this.closedHospitalProduct) {
        return true;
      }
      return false;
    },

    // Checks if current extras product is closed
    isClosedExtrasProduct() {
      if (this.closedExtrasProduct) {
        return true;
      }
      return false;
    },

    eligibleForVeteranCard() {
      return [NeedsAnalysisEligibility.SERVING, NeedsAnalysisEligibility.EX_SERVING].includes(this.changeCoverData.step1.eligibility);
    },

    eligibilityChanged() {
      return this.currentEligibility !== this.changeCoverData.step1.eligibility;
    },

    policyHolderLHCABD() {
      return {
        abd: this.step2.policyHolder.abd,
        lhc: this.step2.policyHolder.lhc,
      };
    },

    eligibleForABD() {
      return this.membershipProducts.some((product) => {
        return product.isEligibleForAbd;
      });
    },

    partnerLHCABD() {
      if (!this.showMyCoverNewPartnerSection) {
        return {
          abd: this.step2.partner.abd,
          lhc: this.step2.partner.lhc,
        };
      }
      // New partner's LHC + ABD information will always be null
      return {
        abd: {},
        lhc: {},
      };
    },

    showVeteranCard() {
      return this.eligibilityChanged && this.eligibleForVeteranCard;
    },

    abdLhcSectionsValid() {
      if (!this.wantsHospital) return true;
      if (this.step2.policyHolder.abdLhcResponseValid !== true) return false;
      if (this.selectedCompositionSupportsPartner && this.step2.partner.keepAdult) {
        if (this.step2.partner.abdLhcResponseValid !== true) return false;
      }
      if (this.showMyCoverNewPartnerSection && (this.eligibleForABD || this.step2.partner.newPartner.eligibleForLHC)) {
        if (this.step2.partner.newPartner.abdLhcResponseValid !== true) return false;
      }
      return true;
    },
  },

  methods: {
    initializeData() {
      const step2 = { ...cloneDeep(this.step2) };

      step2.composition = this.currentComposition;

      if (step2.composition === CoverComposition.FAMILY_PLUS || step2.composition === CoverComposition.SINGLE_PARENT_PLUS) {
        switch (step2.composition) {
          case CoverComposition.FAMILY_PLUS:
            step2.composition = CoverComposition.FAMILY;
            break;
          case CoverComposition.SINGLE_PARENT_PLUS:
            step2.composition = CoverComposition.SINGLE_PARENT;
            break;
          default:
            break;
        }
        step2.familyPlus = true;
      } else if (step2.composition === CoverComposition.FAMILY || step2.composition === CoverComposition.SINGLE_PARENT) {
        step2.familyPlus = false;
      }

      if (this.membershipPolicy.myCircumstances) {
        step2.myCircumstances = this.membershipPolicy.myCircumstances;
      }

      // Rebate tier is returned as an empty string from response
      // Proceed if rebate tier is not null or undefined (empty string accepted)
      if (this.membershipPolicy.rebateTier != null) {
        const contextRebate = this.membershipPolicy.rebateTier;
        step2.rebateTier = isEmptyString(contextRebate) ? RebateTier.NONE : contextRebate;
        this.updateCurrentRebateTier(contextRebate);
      }

      // Get active dependents and add them to vuex for Step 2. read-only Partner Information & No of children
      if (this.memberDetails) {
        const policyHolder = findPolicyHolder(this.memberDetails, true, true);

        if (policyHolder) {
          step2.policyHolder.dob = policyHolder.dob;

          // Check for active LHC details (for retaining hospital cover)
          if (policyHolder.lhc) {
            step2.policyHolder.lhc = formatEaiLhcToObj(policyHolder.lhc);
            this.updateInitialPolicyHolderLhc(step2.policyHolder.lhc);
          }

          // Check for existing overrule reasons
          step2.policyHolder.existingLHCOverrule = policyHolder.lhcOverrule !== null;

          step2.policyHolder.abd = policyHolder.abdDetails;
          this.updateInitialPolicyHolderAbd(policyHolder.abdDetails);

          step2.policyHolder.portalAgeBasedDiscount = policyHolder.portalAgeBasedDiscount;
          if (policyHolder.portalAgeBasedDiscount) {
            step2.policyHolder.portalAgeBasedDiscount.firstTimePHI = policyHolder.portalAgeBasedDiscount.firstTimePHI;
            step2.policyHolder.portalAgeBasedDiscount.currentlyReceived = policyHolder.portalAgeBasedDiscount.currentlyReceived;
            step2.policyHolder.portalAgeBasedDiscount.cda = policyHolder.portalAgeBasedDiscount.cda;
          }
          this.updateInitialPolicyHolderPortalAbd(policyHolder.portalAgeBasedDiscount);

          step2.policyHolder.veteranCard = this.formatVeteranCardDetails(policyHolder);
          // Store in CAI initial vuex for computing difference on submission
          this.updateInitialPolicyHolderVeteranCard(step2.policyHolder.veteranCard);
        }

        const partner = getPartnerDetailsMP(this.memberDetails, true);
        if (partner) {
          step2.partner.firstName = partner.firstName ? partner.firstName : '';
          step2.partner.lastName = partner.lastName ? partner.lastName : '';
          step2.partner.dob = partner.dob ? reverseFormatFormDate(partner.dob) : '';
          step2.partner.keepAdult = true;

          // Check for active LHC details (for retaining hospital cover)
          if (partner.lhc) {
            step2.partner.lhc = formatEaiLhcToObj(partner.lhc);
            this.updateInitialPartnerLhc(step2.partner.lhc);
          }
          // Check for existing overrule reasons
          step2.partner.existingLHCOverrule = partner.lhcOverrule !== null;

          step2.partner.abd = partner.abdDetails;
          this.updateInitialPartnerAbd(partner.abdDetails);

          step2.partner.portalAgeBasedDiscount = partner.portalAgeBasedDiscount;
          if (partner.portalAgeBasedDiscount) {
            step2.partner.portalAgeBasedDiscount.firstTimePHI = partner.portalAgeBasedDiscount.firstTimePHI;
            step2.partner.portalAgeBasedDiscount.currentlyReceived = partner.portalAgeBasedDiscount.currentlyReceived;
            step2.partner.portalAgeBasedDiscount.cda = partner.portalAgeBasedDiscount.cda;
          }
          this.updateInitialPartnerPortalAbd(partner.portalAgeBasedDiscount);

          step2.partner.veteranCard = this.formatVeteranCardDetails(partner);
          this.updateInitialPartnerVeteranCard(step2.partner.veteranCard);
        }

        // Get number of children
        const children = findDependents(this.memberDetails);

        const numberOfChildren = children.length > 0 ? children.length.toString() : null;
        step2.numberOfChildren = numberOfChildren;
        this.updateCurrentNumberOfChildren(numberOfChildren);
      }

      this.step2 = step2;
    },

    getPolicyHolderABDAndLHCResponse(data) {
      this.step2.policyHolder.abdLhcResponseValid = !data.invalid;
      this.step2.policyHolder.abd = { ...this.step2.policyHolder.abd, ...data.abd };
      this.step2.policyHolder.lhc = { ...this.step2.policyHolder.lhc, ...data.lhc };

      this.updatePolicyHolderStep({ ...this.policyHolderStep, lhc: { ...data.lhc }, abd: { ...data.abd } });
    },

    policyHolderUpdateEligibleForLHC(data) {
      this.step2.policyHolder.eligibleForLHC = data;
    },

    getPartnerABDAndLHCResponse(data) {
      if (!this.showMyCoverNewPartnerSection) {
        this.step2.partner.abdLhcResponseValid = !data.invalid;
        this.step2.partner.abd = { ...this.step2.partner.abd, ...data.abd };
        this.step2.partner.lhc = { ...this.step2.partner.lhc, ...data.lhc };
      } else {
        this.step2.partner.newPartner.abdLhcResponseValid = !data.invalid;
        this.step2.partner.newPartner.abd = data.abd;
        this.step2.partner.newPartner.lhc = data.lhc;
      }

      this.updatePartnerStep({ ...this.partnerStep, lhc: { ...data.lhc }, abd: { ...data.abd } });
    },

    partnerUpdateEligibleForLHC(data) {
      if (!this.showMyCoverNewPartnerSection) {
        this.step2.partner.eligibleForLHC = data;
      } else {
        this.step2.partner.newPartner.eligibleForLHC = data;
      }
    },

    veteranCardSupportText(cardType) {
      if (!cardType) {
        return null;
      }

      if (cardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_GOLD_CARD) {
        return this.refData.veteranCardGoldTypeInformationText.informationText;
      }

      if (cardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD) {
        return this.refData.veteranCardWhiteTypeInformationText.informationText;
      }

      return null;
    },

    /**
     *
     */
    formatVeteranCardDetails(memberObj) {
      try {
        const veteranDetails = memberObj?.activeVeteranCard;

        if (veteranDetails) {
          const veteranCardType =
            veteranDetails.cardType === VeteranCardTypeOptions.VETERAN_GOLD_CODE
              ? VeteranCardTypeOptions.VETERAN_GOLD_CARD_PROPER
              : VeteranCardTypeOptions.VETERAN_WHITE_CARD_PROPER;

          const veteranObj = {
            op: OperationType.NONE,
            haveVeteranCard: true,
            veteranCardType,
            id: veteranDetails.id,
            startDate: veteranDetails.startDate,
            veteranCardConditionOptions: [],
          };

          const acceptedConditions = memberObj.veteranCardAcceptedConditions;

          if (veteranCardType === VeteranCardTypeOptions.VETERAN_WHITE_CARD_PROPER && acceptedConditions && acceptedConditions.length > 0) {
            veteranObj.veteranCardConditionOptions = acceptedConditions.map((condition) => {
              return {
                id: condition.id,
                startDate: condition.startDate,
                op: OperationType.NONE,
                returnValue: condition.code,
              };
            });
          }
          return veteranObj;
        }
        return { haveVeteranCard: false, veteranCardType: null, veteranCardConditionOptions: [] };
      } catch (error) {
        logger.error('Error getting veteran card details: ', error);
        return null;
      }
    },
  },

  validations() {
    const validationsObj = {
      step2: {
        composition: {
          required,
        },
        isPartnerRebateApplicant: () => {
          if (this.isPartnerRebateApplicant && this.showPartner) {
            // fail validation (i.e return false) if partner is rebate applicant and is being removed from policy
            return false;
          }
          return true;
        },
        myCircumstances: {
          required: requiredIf(() => {
            return this.isSingle;
          }),
        },
        rebateTier: {
          required,
        },
        numberOfChildren: {
          required: requiredIf(() => this.isChildrenOptionShow),
          integer,
          minValue: minValue(1),
          maxValue: maxValue(15),
        },
        familyPlus: {
          required: requiredIf(() => {
            return this.isFamily;
          }),
        },
        coverType: {
          required,
        },
        reviewCover: {
          required: requiredIf(() => {
            return this.isHospitalAndExtras && (this.isClosedHospitalProduct || this.isClosedExtrasProduct);
          }),
        },
        policyHolder: {
          veteranCard: {
            haveVeteranCard: {
              required: requiredIf(function () {
                return this.showVeteranCard;
              }),
            },
            veteranCardType: {
              required: requiredIf(function () {
                return this.showVeteranCard && this.step2.policyHolder.veteranCard.haveVeteranCard;
              }),
            },
          },
          abdLhcResponseValid: {
            required: requiredIf(function () {
              return this.wantsHospital;
            }),
          },
        },
        partner: {
          keepAdult: {
            required: requiredIf(() => this.selectedCompositionSupportsPartner && !this.step2.partner.newPartner.DOB),
          },
          veteranCard: {
            haveVeteranCard: {
              required: requiredIf(() => this.selectedCompositionSupportsPartner && this.step2.partner.keepAdult && this.showVeteranCard && !this.showMyCoverNewPartnerSection),
            },
            veteranCardType: {
              required: requiredIf(() => this.selectedCompositionSupportsPartner && this.step2.partner.keepAdult && this.showVeteranCard && this.step2.partner.veteranCard.haveVeteranCard),
            },
          },
          abdLhcResponseValid: {
            required: requiredIf(() => this.wantsHospital && this.selectedCompositionSupportsPartner && this.step2.partner.keepAdult),
          },
          newPartner: {
            DOB: {
              required: requiredIf(() => this.showMyCoverNewPartnerSection && this.selectedCompositionSupportsPartner),
              DATE_VALIDATOR,
              overAge,
              underAge,
              isPastDate,
            },
            veteranCard: {
              haveVeteranCard: {
                required: requiredIf(() => this.showMyCoverNewPartnerSection && this.eligibleForVeteranCard),
              },
              veteranCardType: {
                required: requiredIf(() => this.showMyCoverNewPartnerSection && this.eligibleForVeteranCard && this.step2.partner.newPartner.veteranCard.haveVeteranCard),
              },
            },
            abdLhcResponseValid: {
              required: requiredIf(() => this.showMyCoverNewPartnerSection && this.wantsHospital && (this.eligibleForABD || this.step2.partner.newPartner.eligibleForLHC)),
            },
          },
        },
      },
    };

    return validationsObj;
  },
};
</script>

<style lang="scss" scoped>
.student-warning {
  margin-top: rem(10);
}

.grid-content {
  display: contents;
}

.member-container {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;

  .member-icon {
    display: flex;
    box-sizing: border-box;
    justify-content: center;
    align-content: center;
    width: 40px;
    height: 40px;
    padding: 8px;
    background-color: $PRIMARY_GREEN_TINT;

    svg {
      width: 20px;
      height: 20px;
      color: $PRIMARY_GREEN;
    }
  }
}

.veteran-card-type {
  margin-bottom: 1rem;
}
</style>
